import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AuthProvider } from './auth/AuthContext'
import { useAuth } from './auth/useAuth'
import { LoginPage } from './auth/LoginPage'
import { RegisterPage } from './auth/RegisterPage'
import { GoogleCallback } from './auth/GoogleCallback'
import { VerifyEmailPage } from './auth/VerifyEmailPage'
import { ProjectListPage } from './projects/ProjectListPage'
import { NewProjectPage } from './projects/NewProjectPage'
import { ProjectDetailPage } from './projects/ProjectDetailPage'
import { DocumentPreview } from './documents/DocumentPreview'
import { ProfilePage } from './auth/ProfilePage'
import { NotFoundPage } from './pages/NotFoundPage'
import { AdminDashboard } from './admin/AdminDashboard'
import { LoadingSpinner } from './components/LoadingSpinner'
import styles from './App.module.css'

// Lazy-load the workspace: konva (~185KB minified) must NOT ship in the main bundle.
// This keeps the initial load of /projects well under the 200KB gzip target.
const WorkspacePage = lazy(() =>
  import('./workspace/WorkspacePage').then((m) => ({ default: m.WorkspacePage }))
)

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <LoadingSpinner size="lg" message="Cargando..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('fasico:redirect_after_login', window.location.pathname)
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return null
  if (isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<GoogleCallback />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProjectListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/new"
        element={
          <ProtectedRoute>
            <NewProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id/documents"
        element={
          <ProtectedRoute>
            <DocumentPreview />
          </ProtectedRoute>
        }
      />
      {/* Ruta deprecated — redirige permanentemente al workspace */}
      <Route
        path="/projects/:id/floor-plan"
        element={<Navigate to="../workspace" replace />}
      />
      <Route
        path="/projects/:id/workspace"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className={styles.centered}>
                  <LoadingSpinner size="lg" message="Cargando plano..." />
                </div>
              }
            >
              <WorkspacePage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
